import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, Users, ShieldAlert, AlertCircle, Loader2,
  Lock, Eye, Clock, Flag, FileText,
} from 'lucide-react';
import api from '../lib/api';
import { BottomSheet } from '../components/Overlay';
import { formatDistanceToNow, parseISO } from 'date-fns';

/**
 * AdminChats — Trust & Safety moderation surface.
 *
 *   • Groups tab        : full silent read access to every group chat
 *   • Flagged DMs tab   : DMs with an open report; click → reason → messages
 *   • Audit Log tab     : record of every admin DM access (and group reviews)
 *
 * All DM access requires a reason; super-admin Emergency Access is a
 * separate confirmation path with stronger warning copy.
 */
export default function AdminChats() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('groups');
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [groups, setGroups] = useState([]);
  const [dms, setDms] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [openThread, setOpenThread] = useState(null); // {kind:'group'|'dm', id, name, ...}
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [accessModal, setAccessModal] = useState(null); // {threadId, isReported}
  const [reason, setReason] = useState('');
  const [emergency, setEmergency] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/admin/me');
        if (alive) {
          setIsAdmin(!!r.data.is_admin);
          setIsSuperAdmin(!!r.data.is_super_admin || !!r.data.super_admin);
        }
      } catch { setIsAdmin(false); }
      setAuthChecking(false);
    })();
    return () => { alive = false; };
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const r = await api.get('/admin/chats/groups?limit=80');
      setGroups(r.data.items || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadDms = useCallback(async () => {
    try {
      const r = await api.get('/admin/chats/dm/flagged?limit=80');
      setDms(r.data.items || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get('/admin/chats/audit-log?limit=80');
      setAuditLog(r.data.items || []);
      setIsSuperAdmin(prev => prev || !!r.data.viewer_is_super_admin);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === 'groups') loadGroups();
    else if (tab === 'dms') loadDms();
    else if (tab === 'audit') loadAudit();
  }, [tab, isAdmin, loadGroups, loadDms, loadAudit]);

  const openGroup = async (g) => {
    setLoadingThread(true);
    setError('');
    try {
      const r = await api.get(`/admin/chats/groups/${g.id}/messages?limit=300`);
      setOpenThread({ kind: 'group', id: g.id, name: g.name || 'Group', members: g.member_count });
      setMessages(r.data.messages || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load group');
    }
    setLoadingThread(false);
  };

  const openDmIntent = (t, isReported) => {
    setAccessModal({ threadId: t.id, isReported, participants: t.participants_info || [], report: t.report });
    setReason('');
    setEmergency(false);
    setError('');
  };

  const submitDmAccess = async () => {
    if (!accessModal) return;
    if (reason.trim().length < 5) {
      setError('Reason must be at least 5 characters');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const r = await api.post(`/admin/chats/dm/${accessModal.threadId}/access`, {
        reason: reason.trim(),
        emergency: !!emergency,
      });
      setOpenThread({
        kind: 'dm',
        id: accessModal.threadId,
        name: (accessModal.participants || []).map(p => p.display_name).join(' ↔ ') || 'DM',
        emergency_used: r.data.emergency_used,
        is_reported: r.data.is_reported,
      });
      setMessages(r.data.messages || []);
      setAccessModal(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Access denied');
    }
    setSubmitting(false);
  };

  if (authChecking) {
    return <div className="min-h-[100dvh] bg-[#F7F6F2] flex items-center justify-center"><Loader2 size={20} className="text-[#9B9EA3] animate-spin" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle size={28} className="text-[#FF3B30] mb-3" />
        <h1 className="text-lg font-extrabold text-[#111]">Admin access required</h1>
        <button onClick={() => navigate('/')} className="mt-4 px-4 py-2 bg-[#111] text-white rounded-full text-sm font-bold">Back to home</button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2]" data-testid="admin-chats-page">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-black/[0.04]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/settings')} data-testid="back-btn" aria-label="Back" className="w-9 h-9 rounded-full bg-[#F1F0EA] flex items-center justify-center">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold tracking-tight text-[#111]">Chat Moderation</h1>
            <p className="text-[11px] text-[#6B6E73]">{isSuperAdmin ? 'Super-admin · Emergency Access available' : 'Admin · Reported DMs only'}</p>
          </div>
          <ShieldAlert size={18} className="text-[#FF3B30]" />
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3 flex gap-2">
          {[
            { k: 'groups', label: 'Groups', Icon: Users },
            { k: 'dms', label: 'Flagged DMs', Icon: Flag },
            { k: 'audit', label: 'Audit log', Icon: FileText },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => { setTab(t.k); setOpenThread(null); setMessages([]); }}
              data-testid={`tab-${t.k}`}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-extrabold transition ${tab === t.k ? 'bg-[#111] text-white' : 'bg-white text-[#6B6E73] border border-black/[0.06]'}`}
            >
              <t.Icon size={12} />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        {/* Inline thread viewer */}
        {openThread && (
          <div data-testid="thread-viewer" className="bg-white rounded-2xl border border-black/[0.04] overflow-hidden mb-4">
            <div className="px-4 py-3 border-b border-black/[0.04] flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-[#111] truncate">{openThread.name}</p>
                <p className="text-[11px] text-[#6B6E73]">
                  {openThread.kind === 'group'
                    ? `${openThread.members || 0} members · silent read`
                    : (openThread.emergency_used ? '⚡️ Emergency access used' : 'Reported DM · silent read')}
                </p>
              </div>
              <button onClick={() => { setOpenThread(null); setMessages([]); }} data-testid="close-thread" className="text-xs font-bold text-[#6B6E73] hover:underline">Close</button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-4 py-3 space-y-3 hide-scrollbar">
              {loadingThread && <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin text-[#9B9EA3]" /></div>}
              {!loadingThread && messages.length === 0 && (
                <p className="text-center text-xs text-[#9B9EA3] py-6">No messages.</p>
              )}
              {messages.map(m => (
                <div key={m.id} className="flex items-start gap-2.5">
                  {m.user?.profile_photo ? (
                    <img loading="lazy" decoding="async" src={m.user.profile_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[9px] font-bold text-[#9B9EA3] flex-shrink-0">
                      {(m.user?.display_name || '?').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-xs font-extrabold text-[#111]">{m.user?.display_name || m.user_id?.slice(0, 6)}</p>
                      <p className="text-[10px] text-[#9B9EA3]">{m.created_at?.slice(11, 16)}</p>
                    </div>
                    {m.text && <p className="text-sm text-[#111] mt-0.5 break-words">{m.text}</p>}
                    {m.media_url && <p className="text-[11px] text-[#3F5200] mt-0.5">📎 {m.media_url}</p>}
                    {m.audio_url && <p className="text-[11px] text-[#3F5200] mt-0.5">🎙 voice {Math.round((m.audio_duration_ms || 0) / 1000)}s</p>}
                    {m.poll && <p className="text-[11px] text-[#3F5200] mt-0.5">📊 Poll · {m.poll.question}</p>}
                    {m.plan_share && <p className="text-[11px] text-[#3F5200] mt-0.5">📅 Shared plan {m.plan_share.plan_id}</p>}
                    {m.story_ref && <p className="text-[11px] text-[#3F5200] mt-0.5">📸 Story reply</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'groups' && (
          <section data-testid="groups-list" className="space-y-2">
            {groups.length === 0 && <p className="text-center text-sm text-[#9B9EA3] py-6">No groups.</p>}
            {groups.map(g => (
              <button
                key={g.id}
                onClick={() => openGroup(g)}
                data-testid={`group-${g.id}`}
                className="w-full bg-white rounded-2xl border border-black/[0.04] p-3 flex items-center gap-3 text-left active:scale-[0.99] transition"
              >
                {g.photo
                  ? <img loading="lazy" decoding="async" src={g.photo} alt="" className="w-11 h-11 rounded-xl object-cover" />
                  : <div className="w-11 h-11 rounded-xl bg-[#B6E600]/15 flex items-center justify-center"><Users size={18} className="text-[#3F5200]" /></div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-[#111] truncate">{g.name || 'Untitled group'}</p>
                  <p className="text-[11px] text-[#6B6E73] truncate">{g.member_count || 0} members · {g.last_message ? `"${g.last_message.slice(0, 50)}"` : 'no messages'}</p>
                </div>
                <Eye size={14} className="text-[#9B9EA3]" />
              </button>
            ))}
          </section>
        )}

        {tab === 'dms' && (
          <section data-testid="dms-list" className="space-y-2">
            <div className="bg-[#FFF3CD] border border-[#F0D27A]/40 rounded-2xl p-3 mb-2 flex items-start gap-2.5">
              <Lock size={14} className="text-[#7A4F00] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#7A4F00] leading-relaxed">
                Only DMs with an <strong>open report</strong> appear here. Every access requires a reason and is logged.
                {isSuperAdmin && <span className="block mt-1"><strong>Emergency Access</strong> is available for un-reported DMs in serious investigations.</span>}
              </p>
            </div>
            {dms.length === 0 && (
              <div className="text-center py-12 px-6" data-testid="dms-empty">
                <div className="w-14 h-14 rounded-full bg-[#3F5200]/10 flex items-center justify-center mx-auto mb-3">
                  <ShieldAlert size={22} className="text-[#3F5200]" />
                </div>
                <p className="text-sm font-extrabold text-[#111] mb-1">No reported DMs</p>
                <p className="text-xs text-[#6B6E73] leading-relaxed max-w-[280px] mx-auto">When users report a private message, that thread shows up here for review.</p>
              </div>
            )}
            {dms.map(t => (
              <div
                key={t.id}
                data-testid={`dm-${t.id}`}
                className="bg-white rounded-2xl border border-black/[0.04] p-3"
              >
                <div className="flex items-start gap-3">
                  <div className="flex -space-x-2 flex-shrink-0">
                    {(t.participants_info || []).slice(0, 2).map(p => (
                      p.profile_photo
                        ? <img loading="lazy" decoding="async" key={p.id} src={p.profile_photo} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-white" />
                        : <div key={p.id} className="w-9 h-9 rounded-full bg-[#F1F0EA] border-2 border-white text-[10px] font-bold text-[#9B9EA3] flex items-center justify-center">{(p.display_name || '?').slice(0, 2).toUpperCase()}</div>
                    ))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-[#111] truncate">{(t.participants_info || []).map(p => p.display_name).join(' ↔ ') || 'DM'}</p>
                    <p className="text-[11px] text-[#6B6E73] truncate">{t.last_message?.slice(0, 80) || 'no preview'}</p>
                    {t.report && (
                      <p className="text-[10px] text-[#FF3B30] font-bold mt-1 uppercase tracking-wider">
                        {t.report.severity === 'high' ? '⚠️ ' : ''}Flagged · {t.report.reason}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => openDmIntent(t, true)}
                    data-testid={`open-dm-${t.id}`}
                    className="text-[10px] font-extrabold uppercase tracking-wider bg-[#111] text-white px-3 py-1.5 rounded-full flex-shrink-0 active:scale-95"
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}

        {tab === 'audit' && (
          <section data-testid="audit-list" className="space-y-2">
            {auditLog.length === 0 && <p className="text-center text-sm text-[#9B9EA3] py-6">No admin reads logged yet.</p>}
            {auditLog.map(r => (
              <div key={r.id} data-testid={`audit-${r.id}`} className="bg-white rounded-2xl border border-black/[0.04] p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Clock size={11} className="text-[#9B9EA3]" />
                  <p className="text-[10px] text-[#9B9EA3] tabular-nums">{r.created_at ? formatDistanceToNow(parseISO(r.created_at), { addSuffix: true }) : ''}</p>
                  {r.emergency && <span className="text-[9px] uppercase tracking-wider font-extrabold bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full">Emergency</span>}
                </div>
                <p className="text-xs font-extrabold text-[#111]">
                  {r.admin?.display_name || 'admin'} · {r.target_type} {r.target_id ? `· ${r.target_id.slice(0, 8)}` : ''}
                </p>
                {r.reason && <p className="text-[11px] text-[#6B6E73] mt-0.5">&ldquo;{r.reason}&rdquo;</p>}
                <p className="text-[10px] text-[#9B9EA3] mt-0.5">{r.message_count || 0} messages read</p>
              </div>
            ))}
          </section>
        )}
      </main>

      {/* DM Access Reason Sheet — uses global BottomSheet (safe-area, sticky footer, z-above-nav) */}
      <BottomSheet
        open={!!accessModal}
        onClose={() => setAccessModal(null)}
        title="Reason required"
        testId="access-modal"
        footer={
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setAccessModal(null)}
              data-testid="reason-cancel"
              className="py-3 rounded-full bg-[#F1F0EA] font-bold text-sm text-[#111] active:scale-[0.98]"
            >
              Cancel
            </button>
            <button
              onClick={submitDmAccess}
              disabled={submitting || reason.trim().length < 5}
              data-testid="reason-confirm"
              className="py-3 rounded-full bg-[#FF3B30] text-white font-bold text-sm disabled:opacity-50 active:scale-[0.98]"
            >
              {submitting ? 'Reviewing…' : 'Confirm & Access'}
            </button>
          </div>
        }
      >
        {accessModal && (
          <>
            <div className="flex items-start gap-2 mb-3">
              <Lock size={16} className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#6B6E73] leading-relaxed">
                This DM is private. Your access will be recorded in the audit log along with the reason below. Be specific — this entry is reviewable by other admins.
              </p>
            </div>
            {accessModal.report && (
              <div className="bg-[#FFF3CD] rounded-xl p-3 mb-3 border border-[#F0D27A]/40">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#7A4F00]">Linked report</p>
                <p className="text-xs text-[#5C3B00] mt-0.5">{accessModal.report.reason} · {accessModal.report.details?.slice(0, 100)}</p>
              </div>
            )}
            <textarea
              data-testid="reason-input"
              value={reason}
              onChange={e => setReason(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="e.g. Reviewing harassment report from user X"
              className="w-full bg-[#F1F0EA] rounded-2xl p-3 text-sm focus:outline-none focus:bg-[#E8E7DE] resize-none"
            />
            <p className="text-[10px] text-[#9B9EA3] text-right mt-1">{reason.length}/500</p>
            {isSuperAdmin && !accessModal.isReported && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="emergency-toggle"
                  checked={emergency}
                  onChange={e => setEmergency(e.target.checked)}
                  className="w-4 h-4 accent-[#FF3B30]"
                />
                <span className="text-xs font-bold text-[#FF3B30]">
                  ⚡️ Use Emergency Access (this DM has no open report)
                </span>
              </label>
            )}
            {error && <p className="text-xs text-[#FF3B30] mt-2" data-testid="access-error">{error}</p>}
          </>
        )}
      </BottomSheet>
    </div>
  );
}
